import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, BookOpen, Clock, Play, AlertTriangle, Lock } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingBlock } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import { resolveImg } from "@/lib/imageUtils";

export default function LearnerCourseDetailPage() {
  const { assignmentId } = useParams();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/learner/my-courses/${assignmentId}`);
      setPayload(data.data);
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  }, [assignmentId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <DashboardLayout title="Course"><LoadingBlock label="Loading course…" /></DashboardLayout>;
  if (error || !payload) return (
    <DashboardLayout title="Course">
      <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center text-sm text-rose-800" data-testid="learner-course-error">
        <Lock className="mx-auto h-8 w-8 text-rose-600" />
        <div className="mt-2 font-bold">{error || "You don't have access to this course."}</div>
        <Link to="/app/learner/courses" className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#101876] hover:underline">
          <ArrowLeft className="h-3 w-3" /> Back to My Courses
        </Link>
      </div>
    </DashboardLayout>
  );

  const a = payload.assignment;
  const c = payload.course;

  return (
    <DashboardLayout title={c.course_title} breadcrumbs={["My Learning", "Courses", c.course_title]}>
      <div className="space-y-6" data-testid="learner-course-detail">
        <Link to="/app/learner/courses" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#101876]">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to My Courses
        </Link>

        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          {c.banner ? <img src={resolveImg(c.banner)} alt="" className="h-40 w-full object-cover" /> :
            <div className="h-24 w-full bg-gradient-to-br from-[#101876] to-[#329112]" />}
          <div className="p-6 space-y-3">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">{c.category_name}</div>
            <div className="text-2xl font-black text-slate-900">{c.course_title}</div>
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
              <span className="capitalize">{c.course_type?.replace(/_/g, " ")}</span>
              <span>·</span>
              <span className="capitalize">{c.course_level}</span>
              <span>·</span>
              <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{c.estimated_duration_hours}h</span>
              <span>·</span>
              <StatusBadge status={a.status} />
              {a.mandatory_or_optional === "mandatory" && <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 font-bold text-rose-700"><AlertTriangle className="h-3 w-3" /> Mandatory</span>}
              {a.due_date && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">Due {a.due_date}</span>}
            </div>
            {c.short_description && <p className="text-sm text-slate-700">{c.short_description}</p>}
            <div className="pt-2">
              <button
                disabled
                className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-5 py-2.5 text-sm font-bold text-white opacity-90 cursor-not-allowed"
                data-testid="learner-start-learning"
                title="Player will be available in Phase 6"
              >
                <Play className="h-4 w-4" /> Start learning
              </button>
              <p className="mt-2 text-xs text-slate-500">Learning content player (video / PDF / live class / quiz) will be available in <span className="font-semibold">Phase 6</span>.</p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <h3 className="text-sm font-bold uppercase tracking-widest text-[#329112]">Curriculum</h3>
          {(payload.modules || []).length === 0 && <div className="text-xs text-slate-500">Curriculum will appear once your organization publishes course structure.</div>}
          {(payload.modules || []).map((m) => (
            <div key={m.id} className="rounded-md border border-slate-200 bg-slate-50/50 p-4">
              <div className="text-sm font-bold text-slate-900">{m.display_order}. {m.module_title}</div>
              <div className="text-xs text-slate-500">{m.estimated_duration_minutes} min · {m.lessons?.length || 0} lessons</div>
              {m.lessons?.length > 0 && (
                <div className="mt-2 space-y-1">
                  {m.lessons.map((l) => (
                    <div key={l.id} className="flex items-center justify-between rounded bg-white px-3 py-1.5 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="text-slate-400 font-mono">{l.display_order}.</span>
                        <span className="font-semibold text-slate-800">{l.lesson_title}</span>
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-slate-600">{l.lesson_type}</span>
                      </div>
                      <div className="text-slate-400">{l.estimated_duration_minutes} min · <Lock className="inline h-3 w-3" /></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
